import { DEFAULT_NUMBER_LEARNING_CONFIG, ATIVIDADE_LABELS, TENDENCIA_LABELS } from './config';
import {
    clamp,
    coefficientOfVariation,
    getPercentilBand,
    mean,
    minMaxNormalize,
    normalizeSeries,
    stdDev,
    toStars,
} from './normalize';
import type {
    AtividadeClass,
    DezenaIndicators,
    DezenaRankingRow,
    IndicatorValue,
    NumberLearningConfig,
    NumberLearningResult,
    TendenciaClass,
} from './types';

export interface DezenaProfile {
    dezena: number;
    appearances: number[];
    intervals: number[];
    currentDelay: number;
    frequency: number;
    recentCounts: Record<number, number>;
    streaks: number[];
}

const buildIndicator = (raw: number, normalized: number, detail?: string): IndicatorValue => ({
    raw,
    normalized: clamp(normalized),
    detail,
});

const sliceHistorico = (
    historico: number[][],
    maxConcursos: number | null
) => {
    if (!maxConcursos || historico.length <= maxConcursos) {
        return historico;
    }

    return historico.slice(historico.length - maxConcursos);
};

export const buildDezenaProfiles = (
    historico: number[][],
    maxNum: number,
    config: NumberLearningConfig
): DezenaProfile[] => {
    const draws = sliceHistorico(historico, config.maxConcursosAnalisados);
    const totalDraws = draws.length;

    const profiles: DezenaProfile[] = Array.from({ length: maxNum }, (_, index) => ({
        dezena: index + 1,
        appearances: [],
        intervals: [],
        currentDelay: 0,
        frequency: 0,
        recentCounts: Object.fromEntries(config.recentWindows.map((window) => [window, 0])),
        streaks: [],
    }));

    const delay = Array(maxNum).fill(0);
    const currentStreak = Array(maxNum).fill(0);

    for (let drawIndex = 0; drawIndex < totalDraws; drawIndex++) {
        const draw = draws[drawIndex];
        const appeared = new Set<number>();

        for (let num = 1; num <= maxNum; num++) {
            delay[num - 1]++;
        }

        for (let j = 0; j < draw.length; j++) {
            const num = Number(draw[j]);
            if (!Number.isFinite(num) || num < 1 || num > maxNum) {
                continue;
            }

            appeared.add(num);
            const profile = profiles[num - 1];
            const previous = profile.appearances[profile.appearances.length - 1];

            if (previous !== undefined) {
                profile.intervals.push(drawIndex - previous);
            }

            profile.appearances.push(drawIndex);
            profile.frequency++;
            delay[num - 1] = 0;
        }

        for (let num = 1; num <= maxNum; num++) {
            if (appeared.has(num)) {
                currentStreak[num - 1]++;
            } else if (currentStreak[num - 1] > 0) {
                profiles[num - 1].streaks.push(currentStreak[num - 1]);
                currentStreak[num - 1] = 0;
            }
        }
    }

    for (let num = 1; num <= maxNum; num++) {
        const profile = profiles[num - 1];
        profile.currentDelay = delay[num - 1];

        if (currentStreak[num - 1] > 0) {
            profile.streaks.push(currentStreak[num - 1]);
        }

        config.recentWindows.forEach((window) => {
            const start = Math.max(0, totalDraws - window);
            let count = 0;

            for (let i = start; i < totalDraws; i++) {
                if (draws[i].includes(num)) {
                    count++;
                }
            }

            profile.recentCounts[window] = count;
        });
    }

    return profiles;
};

const classifyTendencia = (historicalRate: number, recentRate: number): { class: TendenciaClass; intensity: number } => {
    if (historicalRate === 0 && recentRate === 0) {
        return { class: 'estavel', intensity: 0 };
    }

    const base = historicalRate || 0.0001;
    const deltaPct = ((recentRate - historicalRate) / base) * 100;

    if (deltaPct > 10) {
        return { class: 'crescente', intensity: clamp(Math.abs(deltaPct), 0, 100) };
    }

    if (deltaPct < -10) {
        return { class: 'decrescente', intensity: clamp(Math.abs(deltaPct), 0, 100) };
    }

    return { class: 'estavel', intensity: clamp(Math.abs(deltaPct), 0, 100) };
};

const classifyAtividade = (historicalRate: number, recentRate: number): AtividadeClass => {
    if (historicalRate === 0 && recentRate === 0) {
        return 'normal';
    }

    const ratio = recentRate / (historicalRate || 0.0001);

    if (ratio >= 1.5) return 'muito_alta';
    if (ratio >= 1.15) return 'alta';
    if (ratio <= 0.5) return 'muito_baixa';
    if (ratio <= 0.85) return 'baixa';
    return 'normal';
};

const computeBlockFrequencies = (profile: DezenaProfile, blockSize: number, totalDraws: number) => {
    const blocks: number[] = [];
    let blockStart = 0;

    while (blockStart < totalDraws) {
        const blockEnd = Math.min(blockStart + blockSize, totalDraws);
        let count = 0;

        profile.appearances.forEach((drawIndex) => {
            if (drawIndex >= blockStart && drawIndex < blockEnd) {
                count++;
            }
        });

        blocks.push(count / Math.max(blockEnd - blockStart, 1));
        blockStart += blockSize;
    }

    return blocks;
};

const computeOscillation = (profile: DezenaProfile, windows: number[], totalDraws: number) => {
    const rates = windows
        .filter((window) => window <= totalDraws)
        .map((window) => profile.recentCounts[window] / window);

    if (rates.length <= 1) {
        return 0;
    }

    let totalDiff = 0;
    for (let i = 1; i < rates.length; i++) {
        totalDiff += Math.abs(rates[i] - rates[i - 1]);
    }

    return totalDiff / (rates.length - 1);
};

export const computeNumberLearningRankings = (
    historico: number[][],
    maxNum: number,
    numSorteados: number,
    config: NumberLearningConfig = DEFAULT_NUMBER_LEARNING_CONFIG
): NumberLearningResult | null => {
    if (!historico?.length || !maxNum) {
        return null;
    }

    const draws = sliceHistorico(historico, config.maxConcursosAnalisados);
    const totalDraws = draws.length;
    const profiles = buildDezenaProfiles(historico, maxNum, config);

    const recentWindow = config.recentWindows.find((window) => window <= totalDraws) ?? totalDraws;

    const regularities = profiles.map((profile) => {
        const cv = coefficientOfVariation(profile.intervals);
        return profile.intervals.length > 1 ? 1 / (1 + cv) : 0.5;
    });

    const consistencies = profiles.map((profile) => {
        const sd = stdDev(profile.intervals);
        return profile.intervals.length > 1 ? 1 / (1 + sd) : 0.5;
    });

    const stabilities = profiles.map((profile) => {
        const blocks = computeBlockFrequencies(profile, config.blockSize, totalDraws);
        const sd = stdDev(blocks);
        return blocks.length > 1 ? 1 / (1 + sd) : 0.5;
    });

    const trendScores = profiles.map((profile) => {
        const historicalRate = totalDraws > 0 ? profile.frequency / totalDraws : 0;
        const recentRate = recentWindow > 0 ? (profile.recentCounts[recentWindow] ?? 0) / recentWindow : 0;
        const tendencia = classifyTendencia(historicalRate, recentRate);
        // Inversamente proporcional: tendência crescente (saindo muito) reduz score
        return tendencia.class === 'crescente'
            ? 50 - tendencia.intensity / 2
            : tendencia.class === 'decrescente'
                ? 50 + tendencia.intensity / 2
                : 50;
    });

    const recencies = profiles.map((profile) => totalDraws - profile.currentDelay);
    const delays = profiles.map((profile) => profile.currentDelay);
    const activities = profiles.map((profile) => {
        const historicalRate = totalDraws > 0 ? profile.frequency / totalDraws : 0;
        const recentRate = recentWindow > 0 ? (profile.recentCounts[recentWindow] ?? 0) / recentWindow : 0;
        const atividade = classifyAtividade(historicalRate, recentRate);
        // Inversamente proporcional: atividade alta (saindo muito) reduz score
        const map: Record<AtividadeClass, number> = {
            muito_baixa: 95,
            baixa: 75,
            normal: 50,
            alta: 30,
            muito_alta: 10,
        };
        return map[atividade];
    });

    const oscillations = profiles.map((profile) => computeOscillation(profile, config.recentWindows, totalDraws));
    const persistences = profiles.map((profile) => (profile.streaks.length ? mean(profile.streaks) : 0));

    // Inversamente proporcional: frequência/recente/recência/persistência altas reduzem score;
    // atraso alto (não saiu há tempo) aumenta score.
    const normFrequencies = normalizeSeries(profiles.map((p) => p.frequency), true);
    const normRecent = normalizeSeries(profiles.map((p) => p.recentCounts[recentWindow] ?? 0), true);
    const normDelays = normalizeSeries(delays);
    const normRegularities = normalizeSeries(regularities);
    const normConsistencies = normalizeSeries(consistencies);
    const normStabilities = normalizeSeries(stabilities);
    const normTrends = normalizeSeries(trendScores);
    const normRecencies = normalizeSeries(recencies, true);
    const normActivities = normalizeSeries(activities);
    const normOscillations = normalizeSeries(oscillations, true);
    const normPersistences = normalizeSeries(persistences, true);

    const freqValues = profiles.map((p) => p.frequency);
    const freqMean = mean(freqValues);
    const freqStdAll = stdDev(freqValues);
    const expectedFreqPerDezena = (totalDraws * numSorteados) / maxNum;
    const normUnderExpected = normalizeSeries(
        profiles.map((p) => expectedFreqPerDezena - p.frequency)
    );
    const intervalStdValues = profiles.map((p) => stdDev(p.intervals));
    const maxIntervalStd = Math.max(...intervalStdValues, 1);

    const weightTotal = Object.values(config.weights).reduce((acc, value) => acc + value, 0) || 100;
    const weightFactor = 100 / weightTotal;

    const rows: DezenaRankingRow[] = profiles.map((profile, index) => {
        const historicalRate = totalDraws > 0 ? profile.frequency / totalDraws : 0;
        const recentRate = recentWindow > 0 ? (profile.recentCounts[recentWindow] ?? 0) / recentWindow : 0;
        const tendencia = classifyTendencia(historicalRate, recentRate);
        const atividade = classifyAtividade(historicalRate, recentRate);

        const expectedFreq = (totalDraws * numSorteados) / maxNum;
        const frequencyRelative = totalDraws > 0 ? (profile.frequency / totalDraws) * 100 : 0;
        const freqDiff = profile.frequency - expectedFreq;
        const freqDiffPct = expectedFreq > 0 ? (freqDiff / expectedFreq) * 100 : 0;
        const intervalMean = mean(profile.intervals);
        const intervalStd = stdDev(profile.intervals);
        const intervalCv = coefficientOfVariation(profile.intervals);
        const maiorAtraso = profile.intervals.length ? Math.max(...profile.intervals) : profile.currentDelay;
        const menorAtraso = profile.intervals.length ? Math.min(...profile.intervals) : profile.currentDelay;
        const persistenciaRaw = profile.streaks.length ? mean(profile.streaks) : 0;
        const oscilacaoRaw = oscillations[index];
        const blockRates = config.recentWindows
            .filter((window) => window <= totalDraws)
            .map((window) => profile.recentCounts[window] / window);
        const blockFreqStd = stdDev(blockRates);
        const blockFreqMean = mean(blockRates);
        const freqCv = blockFreqMean > 0 ? blockFreqStd / blockFreqMean : 0;
        const zScore = freqStdAll > 0 ? (profile.frequency - freqMean) / freqStdAll : 0;

        const score =
            normFrequencies[index] * config.weights.frequenciaHistorica * weightFactor / 100
            + normRecent[index] * config.weights.frequenciaRecente * weightFactor / 100
            + normRegularities[index] * config.weights.regularidade * weightFactor / 100
            + normConsistencies[index] * config.weights.consistencia * weightFactor / 100
            + normStabilities[index] * config.weights.estabilidade * weightFactor / 100
            + normTrends[index] * config.weights.tendencia * weightFactor / 100
            + normDelays[index] * config.weights.recencia * weightFactor / 100
            + normActivities[index] * config.weights.atividade * weightFactor / 100
            + normOscillations[index] * config.weights.oscilacao * weightFactor / 100
            + normPersistences[index] * config.weights.persistencia * weightFactor / 100;

        const indicators: DezenaIndicators = {
            frequenciaHistorica: buildIndicator(
                profile.frequency,
                normFrequencies[index],
                `${profile.frequency} aparições (${frequencyRelative.toFixed(2)}%) — acima da média reduz probabilidade`
            ),
            frequenciaRecente: buildIndicator(
                profile.recentCounts[recentWindow] ?? 0,
                normRecent[index],
                `${profile.recentCounts[recentWindow] ?? 0} nos últimos ${recentWindow} concursos — saídas recentes reduzem probabilidade`
            ),
            frequenciaEsperada: buildIndicator(
                expectedFreq,
                normUnderExpected[index],
                `Esperada: ${expectedFreq.toFixed(2)} | Dif.: ${freqDiff.toFixed(2)} (${freqDiffPct.toFixed(2)}%) — abaixo do esperado favorece`
            ),
            atrasoAtual: buildIndicator(
                profile.currentDelay,
                normDelays[index],
                `${profile.currentDelay} concursos sem sair — maior atraso favorece`
            ),
            atrasoMedio: buildIndicator(intervalMean, minMaxNormalize(intervalMean, 0, totalDraws, true), `Média: ${intervalMean.toFixed(2)}`),
            maiorAtraso: buildIndicator(maiorAtraso, minMaxNormalize(maiorAtraso, 0, totalDraws, true), `Máximo: ${maiorAtraso}`),
            menorAtraso: buildIndicator(menorAtraso, minMaxNormalize(menorAtraso, 0, totalDraws, true), `Mínimo: ${menorAtraso}`),
            intervaloMedio: buildIndicator(intervalMean, minMaxNormalize(intervalMean, 0, totalDraws, true), `Média: ${intervalMean.toFixed(2)}`),
            regularidade: buildIndicator((1 / (1 + intervalCv)) * 100, normRegularities[index], `CV intervalos: ${intervalCv.toFixed(3)}`),
            consistencia: buildIndicator((1 / (1 + intervalStd)) * 100, normConsistencies[index], `Desvio intervalos: ${intervalStd.toFixed(3)}`),
            estabilidade: buildIndicator((1 / (1 + blockFreqStd)) * 100, normStabilities[index], `Blocos de ${config.blockSize} concursos`),
            tendencia: buildIndicator(
                tendencia.intensity,
                normTrends[index],
                `${TENDENCIA_LABELS[tendencia.class]} — crescente reduz probabilidade`
            ),
            atividade: buildIndicator(
                normActivities[index],
                normActivities[index],
                `${ATIVIDADE_LABELS[atividade]} — atividade alta reduz probabilidade`
            ),
            recencia: buildIndicator(
                profile.currentDelay,
                normRecencies[index],
                profile.currentDelay === 0
                    ? 'Saiu no último concurso — menor probabilidade no próximo'
                    : `Atraso atual: ${profile.currentDelay} concursos`
            ),
            oscilacao: buildIndicator(oscilacaoRaw, normOscillations[index], `Oscilação: ${oscilacaoRaw.toFixed(4)}`),
            persistencia: buildIndicator(
                persistenciaRaw,
                normPersistences[index],
                `Média consecutiva: ${persistenciaRaw.toFixed(2)} — sequências longas reduzem probabilidade`
            ),
            ausencia: buildIndicator(
                profile.currentDelay,
                normDelays[index],
                `Atual: ${profile.currentDelay} | Média: ${intervalMean.toFixed(2)} | Máx.: ${maiorAtraso}`
            ),
            desvioPadrao: buildIndicator(intervalStd, minMaxNormalize(intervalStd, 0, maxIntervalStd, true), `σ intervalos: ${intervalStd.toFixed(3)}`),
            coeficienteVariacao: buildIndicator(freqCv, minMaxNormalize(freqCv, 0, 1, true), `CV: ${freqCv.toFixed(4)}`),
            zScore: buildIndicator(zScore, minMaxNormalize(zScore, -3, 3, true), `Z-Score: ${zScore.toFixed(3)} — acima da média reduz probabilidade`),
            percentil: buildIndicator(0, 50),
            indiceHistoricoGeral: buildIndicator(score, clamp(score)),
        };

        return {
            dezena: profile.dezena,
            score: clamp(score),
            rank: 0,
            percentilScore: 0,
            classificacaoPercentil: '',
            estrelas: {},
            indicators,
            tendenciaLabel: TENDENCIA_LABELS[tendencia.class],
            atividadeLabel: ATIVIDADE_LABELS[atividade],
        };
    });

    rows.sort((a, b) => b.score - a.score);

    const zScores = rows.map((row) => row.indicators.zScore.raw);
    const zMin = Math.min(...zScores);
    const zMax = Math.max(...zScores);

    rows.forEach((row, index) => {
        row.rank = index + 1;
        row.percentilScore = maxNum > 1 ? (index / (maxNum - 1)) * 100 : 0;
        row.classificacaoPercentil = getPercentilBand(row.percentilScore);
        row.indicators.percentil = buildIndicator(row.percentilScore, 100 - row.percentilScore, row.classificacaoPercentil);
        row.indicators.indiceHistoricoGeral = buildIndicator(row.score, row.score, `Score composto: ${row.score.toFixed(2)}`);
        row.indicators.zScore.normalized = minMaxNormalize(row.indicators.zScore.raw, zMin, zMax, true);
        row.estrelas = {
            frequencia: toStars(row.indicators.frequenciaHistorica.normalized),
            recencia: toStars(row.indicators.recencia.normalized),
            regularidade: toStars(row.indicators.regularidade.normalized),
            tendencia: toStars(row.indicators.tendencia.normalized),
            consistencia: toStars(row.indicators.consistencia.normalized),
            estabilidade: toStars(row.indicators.estabilidade.normalized),
        };
    });

    return {
        rows,
        totalConcursos: totalDraws,
        config,
        confidence: null,
    };
};
